import { formatNumber } from '@/utils/helper'
import { JsonProperty, Serializable } from 'ts-jackson'
import { Fromable } from '../model'

// Slim shape used only for the Package read-side `branch` field. The full
// Branch model lives in services/branch/model.ts but ts-jackson requires a
// @Serializable class for nested deserialization — a plain object literal
// type wouldn't actually be deserialized.
@Serializable()
export class PackageBranchSummary {
  @JsonProperty('id')
  id!: number

  @JsonProperty('name')
  name: string = ''
}

@Serializable()
export class PackageListData extends Fromable {
  @JsonProperty('id')
  id!: number

  @JsonProperty('packageTypeId')
  packageTypeId!: number

  @JsonProperty('name')
  name!: string

  @JsonProperty('status')
  status!: string

  @JsonProperty('description')
  description!: string

  @JsonProperty('isActive')
  isActive!: boolean

  @JsonProperty('accessType')
  accessType!: string

  @JsonProperty('price')
  price!: number

  @JsonProperty('usageLimit')
  usageLimit!: number

  @JsonProperty('amountCredits')
  amountCredits!: number

  @JsonProperty('expireAfterDays')
  expireAfterDays!: number

  @JsonProperty('credit')
  credit!: number

  @JsonProperty('isAllBranches')
  isAllBranches!: boolean

  /** Family-capable package (QTY-2027) — can be sold as a shared family package. */
  @JsonProperty('isFamily')
  isFamily?: boolean

  /**
   * Create/update responses only (QTY-2064): the package was saved but the
   * gate-permission sync with the access-control system failed. Re-saving the
   * package's doors retries it.
   */
  @JsonProperty('accessControlSyncFailed')
  accessControlSyncFailed?: boolean

  @JsonProperty('branchId')
  branchId?: number

  @JsonProperty({ path: 'branch', type: PackageBranchSummary })
  branch?: PackageBranchSummary

  @JsonProperty('packageType')
  packageType!: PackageType

  @JsonProperty('unitType')
  unitType!: 'SESSIONS' | 'TIMES'

  @JsonProperty('classIds')
  classIds?: number[]

  @JsonProperty('gateIds')
  gateIds?: number[]

  @JsonProperty('serviceIds')
  serviceIds?: number[]

  @JsonProperty('packageClasses')
  packageClasses?: PackageClass[]

  @JsonProperty('accessControlDoors')
  accessControlDoors?: string[]
  @JsonProperty('facilityAccess')
  facilityAccess?: string

  @JsonProperty('facilityConditions')
  facilityConditions?: FacilityCondition[]

  @JsonProperty('quotas')
  quotas?: PackageQuota[]

  @JsonProperty('packageQuotas')
  packageQuotas?: PackageQuotaResponse[]

  public getOption() {
    return { label: this.name, value: this.id?.toString() ?? '' }
  }

  public getQuotaLimit(): number {
    return this.packageQuotas?.[0]?.limit ?? this.usageLimit ?? 0
  }

  public getQuotaServiceIds(): number[] {
    return (
      this.packageQuotas?.flatMap((q) => q.quotaServices.map((s) => s.serviceId)) ??
      this.serviceIds ??
      []
    )
  }

  public getQuotaClassIds(): number[] {
    return (
      this.packageQuotas?.flatMap((q) => q.quotaClasses.map((c) => c.classId)) ??
      this.classIds ??
      []
    )
  }

  public getQuotaFacilityIds(): string[] {
    return this.packageQuotas?.flatMap((q) => q.quotaFacilities.map((f) => f.facilityId)) ?? []
  }

  public DataForTable() {
    return {
      id: this.id,
      packageType: this?.packageType?.nameEn,
      name: this.name,
      description: this.description,
      isActive: this.isActive,
      status: this.status,
      accessType: this.accessType,
      price: this.price != null ? `${formatNumber(this.price)} THB` : '-',
      usageLimit: formatNumber(this.usageLimit),
      // TIMES = duration-based (Swimming Duration basis) — the Amount column
      // must render "-" for those regardless of any stale stored count (QTY-1956).
      unitType: this.unitType,
      amountCredits: formatNumber(this.amountCredits),
      expireAfterDays: this.expireAfterDays,
      isAllBranches: this.isAllBranches,
      isFamily: this.isFamily ?? false,
    }
  }

  public serializeForCreate() {
    return {
      packageTypeId: this.packageTypeId,
      unitType: this.unitType,
      name: this.name,
      description: this.description,
      isActive: this.isActive,
      accessType: this.accessType,
      price: this.price,
      amountCredits: this.amountCredits,
      expireAfterDays: this.expireAfterDays,
      isAllBranches: this.isAllBranches,
      isFamily: this.isFamily ?? false,
      // branchId is NOT sent in the payload. For branch-specific packages
      // (isAllBranches=false), the BE reads `branch-id` from the request
      // header and creates the PackageBranch pivot row. The caller is
      // responsible for setting the right header (see
      // `PackageService.createPackage(..., { branchIdOverride })`).
      accessControlDoors: this.accessControlDoors,
      classIds: this.classIds ?? [],
      quotas: this.quotas ?? [],
    }
  }
}

@Serializable()
export class PackageType {
  @JsonProperty('id')
  id?: number

  @JsonProperty('nameEn')
  nameEn!: string

  @JsonProperty('nameTh')
  nameTh!: string
}

@Serializable()
export class PackageClass {
  @JsonProperty('categoryIds')
  categoryIds?: number[]

  @JsonProperty('classType')
  classType?: string

  @JsonProperty('description')
  description?: string

  @JsonProperty('id')
  id?: number

  @JsonProperty('isActive')
  isActive?: boolean

  @JsonProperty('title')
  title?: string

  @JsonProperty('youtubeUrl')
  youtubeUrl?: string
}

export interface FacilityCondition {
  facilityId: string
  quotaAmount?: number
  quotaUnit?: string
  unlimitedAccess: boolean
  gateIds: number[]
}

/**
 * One Wellness & Spa treatment a quota pays for. `durationOptionId: null` covers EVERY length of
 * that treatment; a specific option id restricts the coverage to that one tier.
 */
export interface PackageQuotaSpaService {
  spaServiceId: string
  durationOptionId?: string | null
}

/** Shape used when creating/updating (POST/PATCH payload) */
export interface PackageQuota {
  limit: number
  isUnlimit?: boolean
  quotaResetType?: string
  /** SESSION (default) = `limit` counts bookings; HOUR = it counts booked hours. */
  usageUnit?: 'SESSION' | 'HOUR'
  classIds?: number[]
  serviceIds?: number[]
  facilityIds?: string[]
  spaServices?: PackageQuotaSpaService[]
}

/** Shape returned by GET /packages/:id */
export interface PackageQuotaResponse {
  id: number
  limit: number
  isUnlimit?: boolean
  /** What one unit of `limit` buys: SESSION = a booking, HOUR = a booked hour (rounded up). */
  usageUnit?: 'SESSION' | 'HOUR'
  quotaClasses: Array<{ classId: number; name?: string; class?: PackageClass }>
  quotaResetType: 'DAILY' | 'WEEKLY' | 'MONTHLY' | 'YEARLY' | 'LIFETIME'
  quotaServices: Array<{ serviceId: number; service?: { id: number; name?: string } }>
  quotaFacilities: Array<{
    facilityId: string
    name?: string
    facility?: { id: string; name?: string }
  }>
  /** Absent on packages created before Wellness & Spa, and on non-spa quotas. */
  quotaSpaServices?: Array<{
    spaServiceId: string
    durationOptionId?: string | null
    spaService?: { id: string; name?: string }
  }>
}

export type PackageBuyerStatus = 'ACTIVE' | 'EXPIRED'

/** One purchased membership of a package (row from GET /packages/:id/buyers). */
@Serializable()
export class PackageBuyer extends Fromable {
  @JsonProperty('membershipId')
  membershipId!: number

  @JsonProperty('memberId')
  memberId!: number

  @JsonProperty('memberCode')
  memberCode!: string

  @JsonProperty('memberName')
  memberName!: string

  @JsonProperty('memberPhone')
  memberPhone?: string

  @JsonProperty('purchasedAt')
  purchasedAt!: string

  @JsonProperty('startDate')
  startDate?: string

  @JsonProperty('expireDate')
  expireDate?: string

  @JsonProperty('isActive')
  isActive!: boolean

  /** Remaining uses; null for unlimited/duration packages. */
  @JsonProperty('remainingUses')
  remainingUses?: number | null

  @JsonProperty('saleName')
  saleName?: string

  /** Package unit price in BAHT (backend already returns baht). */
  @JsonProperty('packagePrice')
  packagePrice?: number

  /** Joining fee in BAHT — backend converts from satang server-side; display as-is. */
  @JsonProperty('joiningFee')
  joiningFee?: number
}

/** Aggregate KPIs from GET /packages/:id/buyers/summary. */
@Serializable()
export class PackageBuyersSummary extends Fromable {
  @JsonProperty('totalMemberships')
  totalMemberships: number = 0

  @JsonProperty('distinctBuyers')
  distinctBuyers: number = 0

  @JsonProperty('activeMemberships')
  activeMemberships: number = 0

  @JsonProperty('expiredMemberships')
  expiredMemberships: number = 0

  /** Range-scoped, so this always equals totalMemberships — not shown separately in the UI. */
  @JsonProperty('newInRange')
  newInRange: number = 0

  /** Sum of package prices in BAHT. */
  @JsonProperty('totalPackageValue')
  totalPackageValue: number = 0

  /** Sum of joining fees in BAHT (backend already returns baht). */
  @JsonProperty('totalJoiningFee')
  totalJoiningFee: number = 0
}

export * from './package-type'
